import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthGuard } from '../App';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt, faMoneyBillWave, faSpinner, faCheckCircle, faTimesCircle, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { StandardURL } from "../App";

const ServiceChargePaymentPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [propertyId, setPropertyId] = useState(null);
  const [serviceChargeAmount, setServiceChargeAmount] = useState(null); // State for service charge amount
  const [isAmountLoading, setIsAmountLoading] = useState(false); // State for loading amount

  // Effect to load propertyId and fetch service charge amount
  useEffect(() => {
    const storedPropertyId = localStorage.getItem("selectedPropertyId");
    if (storedPropertyId) {
      setPropertyId(storedPropertyId);
    } else {
      toast.error("No property ID found. Please select a property first. ⚠️");
    }
  }, []);

  // Effect to fetch service charge amount when propertyId is available
  useEffect(() => {
    const fetchServiceCharge = async () => {
      if (!propertyId) return;
      setIsAmountLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("Authentication token not found. Please log in again. 🔑");
        setIsAmountLoading(false);
        return;
      }
      try {
        const response = await axios.get(
          `${StandardURL}/iNyumba/serviceratecharge/fetchspecific/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (response.data && response.data.response) {
          setServiceChargeAmount(response.data.response[0].servicefee); // Assuming the response contains an 'amount' field
        } else {
          toast.error("No service charge amount found for this property. 😔");
        }
      } catch (error) {
        const errorMessage = error.response?.data?.response || "Failed to fetch service charge amount. Please try again later. 🚧";
        toast.error(errorMessage);
      } finally {
        setIsAmountLoading(false);
      }
    };
    fetchServiceCharge();
  }, [propertyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!propertyId) {
      toast.error("Property ID is missing. Cannot initiate payment. 🚫");
      setIsLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Authentication token not found. Please log in again. 🔑");
      setIsLoading(false);
      return;
    }
    if (!/^(?:(?:\+|00)254|0)[1-9]\d{8}$/.test(phoneNumber)) {
      toast.error("Please enter a valid Kenyan phone number (e.g., 07XX... or +2547XX...). 🔢");
      setIsLoading(false);
      return;
    }
    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/stkpushservicecharge/${propertyId}`,
        {
          phone_number: phoneNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data && response.data.success) {
        toast.success(response.data.response || "STK Push initiated successfully! Please check your phone. ✅");
        setPhoneNumber('');
      } else {
        toast.error(response.data.response || "Failed to initiate STK Push. Please try again. 😔");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.response || "An unexpected error occurred. Please try again later. 🚧";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 flex items-center justify-center p-4 sm:p-6">
        <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-3xl w-full max-w-md animate-fade-in-up border border-gray-100">
          <div className="text-center mb-8">
            <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-600 text-6xl mb-4 animate-bounce-in" />
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">Pay Service Charge</h2>
            <p className="text-gray-600 mt-3 text-lg">Initiate M-Pesa STK Push for your service charge payment.</p>
          </div>
          <div className="mb-6 text-center">
            {isAmountLoading ? (
              <div className="flex items-center justify-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-green-600 text-xl mr-2" />
                <span className="text-gray-600">Loading service charge amount...</span>
              </div>
            ) : serviceChargeAmount !== null ? (
              <div className="flex items-center justify-center">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-600 mr-2" />
                <p className="text-lg font-semibold text-gray-800">
                  Service Charge Amount: KES {serviceChargeAmount}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 mr-2" />
                <p className="text-lg text-red-600">No service charge amount available</p>
              </div>
            )}
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="phone-number" className="block text-gray-700 text-sm font-semibold mb-2">
                <FontAwesomeIcon icon={faMobileAlt} className="mr-2 text-gray-500" />
                M-Pesa Phone Number:
              </label>
              <input
                type="tel"
                id="phone-number"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 transition duration-200 outline-none shadow-sm"
                placeholder="e.g., 0712345678 or +254712345678"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                disabled={isLoading}
                aria-label="M-Pesa Phone Number"
              />
              <p className="text-xs text-gray-500 mt-1 flex items-center">
                <FontAwesomeIcon icon={faShieldAlt} className="mr-1 text-green-500" />
                Ensure this is the phone number registered with M-Pesa.
              </p>
            </div>
            <button
              type="submit"
              className={`w-full py-3.5 px-6 rounded-lg text-lg font-bold flex items-center justify-center transition-all duration-300 ease-in-out transform hover:scale-105
                ${isLoading
                  ? 'bg-green-400 cursor-not-allowed opacity-80'
                  : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-300 shadow-lg'
                }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-3 text-xl" />
                  Initiating Payment...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3 text-xl" />
                  Initiate STK Push
                </>
              )}
            </button>
          </form>
          <p className="text-center text-gray-500 text-sm mt-6">
            You will receive a prompt on your phone to complete the payment.
          </p>
        </div>
      </div>
    </AuthGuard>
  );
};

export default ServiceChargePaymentPage;